import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { ActionBar, Container, PageHeader } from '@vnetwork/ui/layout';
import { useToast } from '@vnetwork/ui/feedback';
import { useEventJobConfig, useCreateEventJob } from '@/api/media/live-event-jobs';
import type {
  CreateEventJobRequest,
  EventJob,
  HlsProtection,
  DashProtection,
  RecordingConfig,
  SnapshotConfig,
  StorageConfig,
  OutputType,
} from '@/api/media/live-event-jobs-types';
import { BasicSection } from './BasicSection';
import { StreamConfigSection } from './StreamConfigSection';
import { ProtectionSection } from './ProtectionSection';
import { RecordingSnapshotSection } from './RecordingSnapshotSection';
import { ResultSection } from './ResultSection';

const NAME_RE = /^[a-zA-Z0-9_]{1,64}$/;

const DEFAULT_HLS: HlsProtection = { mode: 'none' };
const DEFAULT_DASH: DashProtection = { mode: 'no_drm' };

type FormErrors = Record<string, string>;
// Default recording storage seeds ftp (legacy default) so enabling recording is never storage-empty
const DEFAULT_RECORDING_STORAGE: StorageConfig = { type: 'ftp', storage_id: '', path: '' };
// Snapshot storage is always swiftserve_cdn with a fixed path (legacy contract)
const SNAPSHOT_STORAGE_PATH = '/pub/__defaultVhost__/{app_name}/{stream_name}/{yyyyMMdd}/';
const DEFAULT_SNAPSHOT_STORAGE: StorageConfig = { type: 'swiftserve_cdn', storage_id: '', path: SNAPSHOT_STORAGE_PATH };
const DEFAULT_RECORDING: RecordingConfig = { enabled: false, mode: 'mp4', storage: DEFAULT_RECORDING_STORAGE };
const DEFAULT_SNAPSHOT: SnapshotConfig = { enabled: false, type: 'jpg', storage: DEFAULT_SNAPSHOT_STORAGE };

const defaultForm = (): CreateEventJobRequest => ({
  app_name: '',
  stream_name: '',
  input_protocol: 'rtmp',
  outputs: [],
  presets: [],
  timeshift_enabled: false,
  low_latency_enabled: false,
  gpu_enabled: false,
});


export interface LiveEventJobCreateProps {
  serviceId: string;
}

/**
 * Create Event Job wizard — Basic info → Stream config → Protection → Recording/Snapshot → Result.
 * All sections render sequentially (no step-by-step tabs); the result section replaces the form
 * on success.
 */
export function LiveEventJobCreate({ serviceId }: LiveEventJobCreateProps) {
  const navigate = useNavigate();
  const { show } = useToast();

  const { data: config } = useEventJobConfig(serviceId);

  const [form, setForm] = useState<CreateEventJobRequest>(defaultForm());
  const [hlsProtection, setHlsProtection] = useState<HlsProtection>(DEFAULT_HLS);
  const [dashProtection, setDashProtection] = useState<DashProtection>(DEFAULT_DASH);
  const [recording, setRecording] = useState<RecordingConfig>(DEFAULT_RECORDING);
  const [snapshot, setSnapshot] = useState<SnapshotConfig>(DEFAULT_SNAPSHOT);
  const [audioOnlyFormat, setAudioOnlyFormat] = useState<'aac' | 'mp3'>('mp3');
  const [errors, setErrors] = useState<FormErrors>({});
  const [createdJob, setCreatedJob] = useState<EventJob | null>(null);

  const createMut = useCreateEventJob(serviceId, {
    onSuccess: (job) => {
      show({ tone: 'success', title: 'Event job created' });
      setCreatedJob(job);
    },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to create event job' }),
  });

  // SRT restricts outputs to hls/dash only — strip rtmp/http-flv on protocol switch
  const SRT_PROTOCOLS = ['srt'];
  const SRT_COMPATIBLE_OUTPUTS: OutputType[] = ['hls', 'dash'];

  const setField = (field: string, value: unknown) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'input_protocol' && SRT_PROTOCOLS.includes(value as string)) {
        next.outputs = prev.outputs.filter((o) => SRT_COMPATIBLE_OUTPUTS.includes(o as OutputType));
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!NAME_RE.test(form.app_name.trim())) e.app_name = 'Only letters, digits, underscores — max 64 characters.';
    if (!NAME_RE.test(form.stream_name.trim())) e.stream_name = 'Only letters, digits, underscores — max 64 characters.';
    if (form.outputs.length === 0) e.outputs = 'Select at least one output type.';
    if (form.presets.length === 0) e.presets = 'Select at least one preset.';

    // HLS protection field validation
    if (form.outputs.includes('hls') && hlsProtection.mode !== 'none') {
      const mode = hlsProtection.mode;
      if (mode === 'aes_default_key_server' && !hlsProtection.resource_id?.trim()) {
        e['hls.resource_id'] = 'Resource ID is required.';
      }
      if (mode === 'aes_third_party_key_server') {
        if (!hlsProtection.third_party_key?.trim()) e['hls.third_party_key'] = 'Third-party key is required.';
        if (!hlsProtection.key_url_in_playlist?.trim()) e['hls.key_url_in_playlist'] = 'Key URL in playlist is required.';
      }
      if (mode === 'fairplay' && !hlsProtection.resource_id?.trim()) {
        e['hls.resource_id'] = 'Resource ID is required.';
      }
      if (mode === 'verimatrix') {
        if (!hlsProtection.verimatrix_id?.trim()) e['hls.verimatrix_id'] = 'Verimatrix entry is required.';
        if (!hlsProtection.resource_id?.trim()) e['hls.resource_id'] = 'Resource ID is required.';
      }
    }

    // DASH DRM content ID validation
    if (form.outputs.includes('dash')) {
      if (dashProtection.widevine?.enabled && !dashProtection.widevine.content_id?.trim()) {
        e['dash.widevine.content_id'] = 'Widevine Content ID is required.';
      }
      if (dashProtection.playready?.enabled && !dashProtection.playready.content_id?.trim()) {
        e['dash.playready.content_id'] = 'PlayReady Content ID is required.';
      }
    }

    // Storage validation when recording/snapshot enabled
    if (recording.enabled && !recording.storage?.storage_id) {
      e['recording.storage_id'] = 'Select a recording storage.';
    }
    if (snapshot.enabled && !snapshot.storage?.storage_id) {
      e['snapshot.storage_id'] = 'Select a snapshot storage.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      show({ tone: 'warning', title: 'Please fill all required fields correctly.' });
      return;
    }

    const streamName = form.stream_name.trim();
    const payload: CreateEventJobRequest = {
      ...form,
      app_name: form.app_name.trim(),
      stream_name: streamName,
      // Legacy contract: name mirrors stream_name (index.tsx:121)
      name: streamName,
    };

    // Include audio_only block only when preset is selected (payload reachable)
    if (form.presets.includes('audio_only')) {
      payload.audio_only = { enabled: true, format: audioOnlyFormat };
    }

    // hls_protection/dash_protection always sent when the output is selected,
    // including {mode:'none'}/{mode:'no_drm'} defaults (legacy index.tsx:130-137)
    if (form.outputs.includes('hls')) {
      payload.hls_protection = hlsProtection;
    }
    if (form.outputs.includes('dash')) {
      payload.dash_protection = dashProtection;
    }

    if (recording.enabled) payload.recording = recording;
    if (snapshot.enabled) payload.snapshot = snapshot;

    createMut.mutate(payload);
  };

  if (createdJob) {
    return (
      <Container size="lg">
        <PageHeader
          icon={<Radio size={22} />}
          title="Event Job Created"
          description="Your live event transcoding job is ready — connect an encoder using the stream details below."
        />
        <ResultSection job={createdJob} />
        <ActionBar end={<Button onClick={() => navigate(`/services/live/${serviceId}/current-jobs`)}>Done</Button>} />
      </Container>
    );
  }

  return (
    <Container size="lg">
      <PageHeader
        icon={<Radio size={22} />}
        title="Create Event Job"
        description="Set up a one-off live event transcoding job — stream inputs, output formats, protection and recording."
      />

      <BasicSection
        form={form}
        errors={{ app_name: errors.app_name, stream_name: errors.stream_name }}
        onChange={setField}
        clearError={(f) => setErrors((p) => { const n = { ...p }; delete n[f]; return n; })}
      />

      <StreamConfigSection
        inputProtocol={form.input_protocol}
        outputs={form.outputs}
        presets={form.presets}
        audioOnlyFormat={audioOnlyFormat}
        lowLatencyEnabled={form.low_latency_enabled}
        config={config}
        errors={{ outputs: errors.outputs, presets: errors.presets }}
        onChange={(field, value) => {
          if (field === 'audioOnlyFormat') { setAudioOnlyFormat(value as 'aac' | 'mp3'); return; }
          setField(field, value);
        }}
      />

      <ProtectionSection
        outputs={form.outputs}
        hlsProtection={hlsProtection}
        dashProtection={dashProtection}
        verimatrixes={config?.verimatrixes}
        onHls={setHlsProtection}
        onDash={setDashProtection}
        errors={errors}
      />

      <RecordingSnapshotSection
        recording={recording}
        snapshot={snapshot}
        config={config}
        onRecording={(patch) => setRecording((p) => ({ ...p, ...patch }))}
        onSnapshot={(patch) => setSnapshot((p) => ({ ...p, ...patch }))}
      />

      <ActionBar
        start={
          <Button variant="secondary" onClick={() => navigate(`/services/live/${serviceId}/current-jobs`)} disabled={createMut.isPending}>
            Cancel
          </Button>
        }
        end={
          <Button onClick={handleSubmit} disabled={createMut.isPending}>
            {createMut.isPending ? 'Creating…' : 'Create Event Job'}
          </Button>
        }
      />
    </Container>
  );
}
